// Tile values (mirrors shared const enum, redefined to avoid build issues)
const Tile = {
  Floor: 0,
  Wall: 1,
  Target: 2,
  Box: 3,
  BoxOnTarget: 4,
  Player: 5,
  PlayerOnTarget: 6,
} as const;

type TileValue = (typeof Tile)[keyof typeof Tile];

export type Direction = 'up' | 'down' | 'left' | 'right';

export interface MoveResult {
  success: boolean;
  pushed: boolean;
  completed: boolean;
}

interface Position {
  row: number;
  col: number;
}

interface HistoryEntry {
  grid: TileValue[][];
  playerPos: Position;
  moves: number;
  pushes: number;
}

const DIRECTION_DELTAS: Record<Direction, { dr: number; dc: number }> = {
  up: { dr: -1, dc: 0 },
  down: { dr: 1, dc: 0 },
  left: { dr: 0, dc: -1 },
  right: { dr: 0, dc: 1 },
};

function cloneGrid(grid: TileValue[][]): TileValue[][] {
  return grid.map((row) => row.slice());
}

function findPlayer(grid: TileValue[][]): Position {
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      if (grid[r][c] === Tile.Player || grid[r][c] === Tile.PlayerOnTarget) {
        return { row: r, col: c };
      }
    }
  }
  throw new Error('No player found in grid');
}

export class SokobanEngine {
  private _grid: TileValue[][];
  private _initialGrid: TileValue[][];
  private _playerPos: Position;
  private _moves: number;
  private _pushes: number;
  private _history: HistoryEntry[];
  private _optimalMoves: number;

  /** Maximum number of undos allowed. Set to Infinity for unlimited. */
  maxUndos: number;

  constructor(grid: number[][], optimalMoves: number = 0) {
    this._initialGrid = cloneGrid(grid as TileValue[][]);
    this._grid = cloneGrid(this._initialGrid);
    this._playerPos = findPlayer(this._grid);
    this._moves = 0;
    this._pushes = 0;
    this._history = [];
    this._optimalMoves = optimalMoves;
    this.maxUndos = 3;
  }

  // --------------- Public readonly properties ---------------

  get grid(): readonly (readonly TileValue[])[] {
    return this._grid;
  }

  get playerPos(): Readonly<Position> {
    return { ...this._playerPos };
  }

  get moves(): number {
    return this._moves;
  }

  get pushes(): number {
    return this._pushes;
  }

  get isComplete(): boolean {
    return this._checkComplete();
  }

  get undosRemaining(): number {
    if (this.maxUndos === Infinity) return Infinity;
    // History is capped at maxUndos entries, so remaining = current entries available
    return this._history.length;
  }

  get rows(): number {
    return this._grid.length;
  }

  get cols(): number {
    return this._grid[0].length;
  }

  // --------------- Core actions ---------------

  /**
   * Attempt to move the player in the given direction.
   * Returns whether the move succeeded, whether a box was pushed,
   * and whether the level is now complete.
   */
  move(direction: Direction): MoveResult {
    const fail: MoveResult = { success: false, pushed: false, completed: false };

    const { dr, dc } = DIRECTION_DELTAS[direction];
    const { row: pr, col: pc } = this._playerPos;
    const nr = pr + dr;
    const nc = pc + dc;

    // Out of bounds check
    if (!this._inBounds(nr, nc)) return fail;

    const targetTile = this._grid[nr][nc];

    // Wall — blocked
    if (targetTile === Tile.Wall) return fail;

    let pushed = false;

    // Box or BoxOnTarget — try to push
    if (targetTile === Tile.Box || targetTile === Tile.BoxOnTarget) {
      const behindR = nr + dr;
      const behindC = nc + dc;

      if (!this._inBounds(behindR, behindC)) return fail;

      const behindTile = this._grid[behindR][behindC];

      // Can only push into Floor or Target
      if (behindTile !== Tile.Floor && behindTile !== Tile.Target) return fail;

      // Save state for undo before modifying anything
      this._pushHistory();

      // Move the box
      this._grid[behindR][behindC] =
        behindTile === Tile.Target ? Tile.BoxOnTarget : Tile.Box;

      // The cell the box was on becomes Floor or Target
      this._grid[nr][nc] =
        targetTile === Tile.BoxOnTarget ? Tile.Target : Tile.Floor;

      pushed = true;
    } else if (targetTile === Tile.Floor || targetTile === Tile.Target) {
      // Simple move — save state first
      this._pushHistory();
    } else {
      // Any other tile (shouldn't happen in a valid level)
      return fail;
    }

    // Move the player out of the old cell
    this._grid[pr][pc] =
      this._grid[pr][pc] === Tile.PlayerOnTarget ? Tile.Target : Tile.Floor;

    // Move the player into the new cell
    this._grid[nr][nc] =
      this._grid[nr][nc] === Tile.Target ? Tile.PlayerOnTarget : Tile.Player;

    this._playerPos = { row: nr, col: nc };
    this._moves++;
    if (pushed) this._pushes++;

    const completed = this._checkComplete();

    return { success: true, pushed, completed };
  }

  /**
   * Undo the last move. Returns true if an undo was performed.
   */
  undo(): boolean {
    if (this._history.length === 0) return false;

    const entry = this._history.pop()!;
    this._grid = entry.grid;
    this._playerPos = entry.playerPos;
    this._moves = entry.moves;
    this._pushes = entry.pushes;

    return true;
  }

  /**
   * Reset the level to its initial state.
   */
  reset(): void {
    this._grid = cloneGrid(this._initialGrid);
    this._playerPos = findPlayer(this._grid);
    this._moves = 0;
    this._pushes = 0;
    this._history = [];
  }

  /**
   * Placeholder for hint system. Returns null.
   */
  getHint(): Direction[] | null {
    return null;
  }

  /**
   * Star rating based on move count vs optimal.
   * 3 stars: at or under optimal
   * 2 stars: within 1.5x optimal
   * 1 star: anything else
   * Returns 0 if optimal is not set or level is not complete.
   */
  getStarRating(): number {
    if (!this._checkComplete()) return 0;
    if (this._optimalMoves <= 0) return 1;

    if (this._moves <= this._optimalMoves) return 3;
    if (this._moves <= this._optimalMoves * 1.5) return 2;
    return 1;
  }

  /**
   * Set the optimal move count (used for star rating).
   */
  setOptimalMoves(optimal: number): void {
    this._optimalMoves = optimal;
  }

  // --------------- Private helpers ---------------

  private _inBounds(r: number, c: number): boolean {
    return r >= 0 && r < this._grid.length && c >= 0 && c < this._grid[r].length;
  }

  private _checkComplete(): boolean {
    for (let r = 0; r < this._grid.length; r++) {
      for (let c = 0; c < this._grid[r].length; c++) {
        // If any bare Target remains (no box on it), not complete
        if (
          this._grid[r][c] === Tile.Target ||
          this._grid[r][c] === Tile.PlayerOnTarget
        ) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Push a snapshot onto the undo history, respecting maxUndos.
   */
  private _pushHistory(): void {
    // If we've reached the max, drop the oldest entry
    if (this.maxUndos !== Infinity && this._history.length >= this.maxUndos) {
      this._history.shift();
    }

    this._history.push({
      grid: cloneGrid(this._grid),
      playerPos: { ...this._playerPos },
      moves: this._moves,
      pushes: this._pushes,
    });
  }
}
